import React, { useCallback } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from '@g4/moment-utc';
import { hook, withValue } from '@g4/automation-hooks';
import { TrackClick } from '@g4/trackster';
import { getPrice } from '@g4/next-furniture/Price/Price';

import { useTranslations } from '../../../graphql/components/TranslationsProvider';
import { Box, Text, useMedia, ConditionalWrap } from '../../Boneyard';
import {
  TripSummaryButton,
  ArrowIconContainer,
  ExpandoLabel,
  ArrowIcon,
} from '../../../pageComponents/paymentPage/TripSummary';
import { useUiCart } from '../../../graphql/localState/uiCart.hooks';
import { useBookingPath } from '../../../graphql';

import { interpolate } from '../../../utils/stringUtils';
import CartItem from './cart/CartItem';
import FlightInfo from './cart/FlightInfo';
import FlightPackagePriceBreakdown from './cart/FlightPackagePriceBreakdown';
import BundleInfo from './cart/BundleInfo';
import TravelersInfo from './cart/TravelersInfo';
import HotelInfo from './cart/HotelInfo';
import CarInfo from './cart/CarInfo';
import Promos from './cart/Promos';
import ShowInfo from './cart/ShowInfo';
import CartFooter from './cart/CartFooter';
import useBookingSteps from '../../../hooks/useBookingSteps';

import { getTripSummary } from '../../../utils/tripSummaryUtils';

import CART_ICON from '../../../images/ic-close-blue.svg';
import StadiumBanner from './cart/StadiumBanner';
import { shouldUpliftBannerDisplay } from '../../../utils/upliftUtils';

import {
  CART_ITEM,
  CART_CLOSE_BUTTON,
  CART_HEADING,
} from '../../../../automationHooks.json';

const propTypes = {
  onRequestClose: PropTypes.func,
  data: PropTypes.shape({
    items: PropTypes.array,
    travelers: PropTypes.array,
  }),
  automationHook: PropTypes.string,
  isTripSummary: PropTypes.bool,
  isMinimumElementsForStadiumPackage: PropTypes.bool,
  allegiantStadiumLogo: PropTypes.object,
  isUpliftInitialised: PropTypes.bool,
  setIsUpliftInitialised: PropTypes.func,
  isPaymentStep: PropTypes.bool,
};

const CART_BOTTOM_MARGIN = 100;
const CartContainer = styled.div`
  position: ${props => (props.isTripSummary ? 'static' : 'absolute')};
  z-index: ${props => (props.isTripSummary ? 0 : 1)};
  top: 0;
  right: 0;
  width: 100%;
  height: auto;
  box-shadow: ${props =>
    props.isTripSummary ? 'none' : '0 0 4px 0 rgba(0, 0, 0, 0.4)'};
  color: ${props => props.theme.colors.cart.text};

  ${props => props.theme.query.sm} {
    top: ${props => props.theme.layout.headerBarHeight}px;
    right: ${props => props.theme.spacing(3)};
    max-width: ${props => (props.isTripSummary ? 'none' : '470px')};

    /* Cart arrow */
    &::before {
      content: '';
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 0 10px 10px 10px;
      border-color: transparent transparent
        ${props => props.theme.colors.cart.primaryBackground} transparent;
      position: absolute;
      top: -10px;
      right: 130px;
      transform: translateX(50%);
    }
  }

  ${props => props.theme.query.md} {
    top: ${props => props.theme.layout.headerBarHeightMd}px;
  }

  ${props => props.theme.query.md} {
    &:before {
      right: 60px;
    }
  }
`;

const CartContent = styled.div`
  width: 100%;
  height: auto;
  background: ${props => props.theme.colors.cart.secondaryBackground};

  ${props => props.theme.query.lg} {
    max-height: ${props =>
    props.isTripSummary
      ? '100%'
      : `calc(100vh - ${props.theme.layout.headerBarHeightMd}px -
        ${CART_BOTTOM_MARGIN}px)`};

    overflow-y: ${props =>
    props.isTripSummary || props.isCartEmpty ? 'auto' : 'scroll'};
  }
`;

const CartHeader = styled.div`
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing(3)};
  background-color: ${props => props.theme.colors.cart.primaryBackground};
`;

const CloseButton = styled.button.attrs({
  type: 'button',
  'aria-label': 'Close',
})`
  width: 24px;
  min-width: 24px;
  height: 24px;
  background-image: url(${CART_ICON});
  background-size: cover;
`;

const BundleItemBox = styled.div`
  height: 110px;
  align-content: space-evenly;
`;

const BundleDivider = styled.hr`
  border: none;
  border-top: 1px solid${props => props.theme.colors.common.disabled};
  margin: 16px 0;
  width: 100%;
`;

const BundleRibbon = styled.div`
  background: ${(props) => {
    const BUNDLE_TIERS = [1, 2, 3];
    const DEFAULT_BUNDLE_TIER = 2;
    const bundleTier = BUNDLE_TIERS.includes(props.tier) ? props.tier : DEFAULT_BUNDLE_TIER;
    return props.theme.colors.bundle[`tier_${bundleTier}`]?.label;
  }};
  margin-left: ${(props) => props.theme.spacing(5)};
  height: 35px;
  width: 90px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  border-radius: 8px;

`;

const BundleLogo = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${(props) => props.imgSrc});
  background-size: 150%;
  background-position: center;
  background-repeat: no-repeat;
`;

const getDaysRange = (startDate, endDate) => {
  const startDateMoment = moment(startDate);
  const endDateMoment = moment(endDate);
  return endDateMoment.diff(startDateMoment, 'days') + 1;
};

const isSeatSelectionInBundle = bundleAncillaries => {
  return bundleAncillaries.some(ancillary => ancillary.type === 'SEAT');
};

const Cart = ({
  onRequestClose = Function.prototype,
  data = {},
  automationHook,
  isTripSummary = false,
  isMinimumElementsForStadiumPackage,
  allegiantStadiumLogo,
  isUpliftInitialised,
  setIsUpliftInitialised,
  isPaymentStep,
}) => {
  const mq = useMedia();
  const isMobile = mq.lt('md');

  const [{ isOpen: isTripSummaryOpen }, updateUiCart] = useUiCart();
  const setIsTripSummaryExpanded = useCallback(
    isOpen => {
      updateUiCart({ isOpen });
    },
    [updateUiCart]
  );
  const toggleIsExpanded = () => {
    setIsTripSummaryExpanded(!isTripSummaryOpen);
  };
  const { steps, activeStep } = useBookingSteps();
  const getStep = key => steps.find(x => x.key === key);

  const { isStadium, isHotelAvailable, isVehicleAvailable } = useBookingPath();

  const showStepLink = stepKey => {
    return (
      steps.indexOf(steps.find(step => step.key === stepKey)) <=
      steps.indexOf(steps.find(step => step.key === activeStep?.key))
    );
  };

  const translations = useTranslations();

  const {
    noCartItemsDescription,
    cartHeading,
    flightInfoCartItemLabel,
    bundleCartItemLabel,
    travelersCartItemLabel,
    hotelCartItemLabel,
    vehicleCartItemLabel,
    vehicleDaysLabel,
    vehicleDaysPluralLabel,
    flightLabel,
    flightsLabel,
    hotelLabel,
    cartItemEditLinkLabel,
    cartItemAddLinkLabel,
    travelerCartItemLabel,
  } = isTripSummary ? translations.paymentPage : translations.cart;

  const {
    cartBannerLabel,
    stadiumTicketsLabel,
    ticketLabel,
    ticketsLabel,
    stadiumPackageLabel,
    stadiumNoCartItemsDescription,
  } = translations.stadiumPackages;

  const { departingLegLabel, returningLegLabel } = translations.common;

  // Commenting this code block as we are not tracking stadium path in GA4 phase 1 release.

  // tracking.setDataLayerVars({
  //   // CD88
  //   stadium_package_ticket_tier: data?.order?.items?.find(
  //     item => item.__typename === 'ShowOrderItem'
  //   )?.show.meta['SECTION_NUMBER'],
  // });

  if (!data.order || !data.order.items.length) {
    return (
      <CartContainer {...hook(automationHook)} isTripSummary={isTripSummary}>
        <CartContent isCartEmpty>
          <CartHeader {...hook(automationHook, 'empty-state')}>
            <Text size={isStadium ? 18 : 20} weight={isStadium ? 400 : 500}>
              {isStadium
                ? stadiumNoCartItemsDescription
                : noCartItemsDescription}
            </Text>
            <TrackClick
              category="site_click_tracking"
              action="Trip Summary"
              label="Close Cart"
            >
              <CloseButton
                onClick={onRequestClose}
                {...hook(CART_CLOSE_BUTTON.hook)}
              />
            </TrackClick>
          </CartHeader>
          {isStadium && (
            <StadiumBanner
              image={allegiantStadiumLogo}
              label={cartBannerLabel}
              showStadiumBanner={allegiantStadiumLogo || cartBannerLabel}
              automationHook={withValue(automationHook, 'raiders-banner')}
            />
          )}
        </CartContent>
      </CartContainer>
    );
  }

  const { travelers, price, payments } = data.order;

  const {
    flightOrderItems,
    seatOrderItems,
    travelerAncillaryOrderItems,
    itineraryAncillaryOrderItems,
    hotelItem,
    vehicleItem,
    showItem,
    bundleOrderItems,
    bundledSeats,
    bundledBagsAndExtras,
    ssrsPriceTotal,
    ancillaryOrderItemsPriceTotal,
    flightHotelCarTotal,
    flightsDiscountAmount,
    governmentFeesItems,
    governmentFeesTotal,
    airlineFeesItems,
    airlineFeesTotal,
    isRoundTripDiscountType,
    isBOGOApplied,
    bogoFlightDiscountAmount,
  } = getTripSummary(data.order);

  const upliftSettings = data?.settings?.uplift;

  let showLabel = '';

  const ticketQuantity = showItem?.length;

  const hasShowItem = Boolean(showItem.length);

  if (isStadium) {
    showLabel = stadiumPackageLabel;
  }

  const showTicketsLabel =
    ticketQuantity + ' ' + (ticketQuantity > 1 ? ticketsLabel : ticketLabel);

  const vehicleDaysCount =
    vehicleItem &&
    getDaysRange(vehicleItem.pickUpDate, vehicleItem.dropOffDate);

  const vehicleDaysCountLabel =
    vehicleDaysCount === 1 ? vehicleDaysLabel : vehicleDaysPluralLabel;

  const departingFlight = [...(flightOrderItems || [])]
    .filter(item => item?.flight?.departingTime)
    .sort((a, b) =>
      moment(a.flight.departingTime).diff(moment(b.flight.departingTime))
    )[0];

  const destinationCode = departingFlight.flight.destination.code;

  const flightHotelCarLabel = [
    flightOrderItems.length < 2 ? flightLabel : flightsLabel,
    hotelItem && hotelLabel,
    vehicleItem && vehicleCartItemLabel,
  ]
    .filter(Boolean)
    .join(' + ');

  const flightHotelCarShowLabel = [
    flightOrderItems.length < 2 ? flightLabel : flightsLabel,
    hotelItem && hotelLabel,
    vehicleItem && vehicleCartItemLabel,
    hasShowItem && showTicketsLabel,
  ]
    .filter(Boolean)
    .join(' + ');

  // Travelers
  const hasValidTravelers = travelers.every(
    item => item.firstName && item.lastName
  );

  const ancillaryOrderItems = [
    ...seatOrderItems,
    ...travelerAncillaryOrderItems,
    ...itineraryAncillaryOrderItems,
  ];

  const promoCodes = Array.isArray(payments)
    ? payments.filter(paymentMethod => paymentMethod.id === 'PROMO')
    : [];
  const hotelPromos = hotelItem ? hotelItem.hotel?.promos : [];
  const vehiclePromos = vehicleItem ? vehicleItem.promotions : [];
  const promos = [...promoCodes, ...hotelPromos, ...vehiclePromos];

  return (
    <CartContainer
      {...hook(automationHook)}
      isTripSummary={isTripSummary}
      isUpliftInitialised={isUpliftInitialised}
      setIsUpliftInitialised={setIsUpliftInitialised}
    >
      {!isTripSummary && (
        <CartHeader>
          <Text
            uppercase
            size={20}
            weight={600}
            automationHook={CART_HEADING.hook}
          >
            {cartHeading}
          </Text>
          <TrackClick
            category="site_click_tracking"
            action="Trip Summary"
            label="Close Cart"
          >
            <CloseButton
              onClick={onRequestClose}
              {...hook(CART_CLOSE_BUTTON.hook)}
            />
          </TrackClick>
        </CartHeader>
      )}

      <CartContent isTripSummary={isTripSummary}>
        <ConditionalWrap
          condition={isTripSummary}
          wrap={children => (
            <CartItem
              label={
                hasShowItem ? flightHotelCarShowLabel : flightHotelCarLabel
              }
              showLabel={showLabel}
              hasShowItem={hasShowItem}
              amount={flightHotelCarTotal}
              withChildrenPadding={false}
              isTripSummary={isTripSummary}
              hidePrice={isStadium && isMinimumElementsForStadiumPackage}
              automationHook={withValue(CART_ITEM.hook, 'flight-package')}
            >
              <Box
                d={'flex'}
                direction={mq.gte('md') ? 'row-reverse' : 'column'}
              >
                {children}
              </Box>
            </CartItem>
          )}
        >
          <ConditionalWrap
            condition={!isTripSummary}
            wrap={children => (
              <CartItem
                showStepLink={showStepLink('flights')}
                label={
                  hasShowItem ? flightHotelCarShowLabel : flightHotelCarLabel
                }
                showLabel={showLabel}
                hasShowItem={hasShowItem}
                amount={flightHotelCarTotal}
                withChildrenPadding={false}
                isTripSummary={isTripSummary}
                hidePrice={isStadium && isMinimumElementsForStadiumPackage}
                automationHook={withValue(CART_ITEM.hook, 'flight-package')}
              >
                {children}
              </CartItem>
            )}
          >
            <Box
              d="flex"
              direction="column"
              w={isTripSummary ? (mq.gte('md') ? '50%' : '100%') : '100%'}
            >
              <FlightPackagePriceBreakdown
                destination={destinationCode}
                airlineFeesItems={airlineFeesItems}
                airlineFeesTotal={airlineFeesTotal}
                governmentFeesItems={governmentFeesItems}
                governmentFeesTotal={governmentFeesTotal}
                flightsDiscountAmount={flightsDiscountAmount}
                isRoundTripDiscountType={isRoundTripDiscountType}
                isTripSummary={isTripSummary}
                flightHotelCarLabel={
                  hasShowItem ? flightHotelCarShowLabel : flightHotelCarLabel
                }
                flightHotelCarTotal={flightHotelCarTotal}
                hidePrice={isStadium && isMinimumElementsForStadiumPackage}
                isStadium={isStadium}
                isBOGOApplied={isBOGOApplied}
                bogoFlightDiscountAmount={bogoFlightDiscountAmount}
              />
              {!isTripSummary ? null : promos.length ? (
                <Promos promos={promos} isTripSummary={isTripSummary} />
              ) : null}
            </Box>
          </ConditionalWrap>
          <ConditionalWrap
            condition={isTripSummary}
            wrap={children => (
              <Box w={mq.gte('md') ? '50%' : '100%'}>{children}</Box>
            )}
          >
            <CartItem
              showStepLink={showStepLink('flights')}
              transparent={isTripSummary && mq.gte('md')}
              uppercase={isTripSummary ? (mq.gte('md') ? false : true) : true}
              isTripSummary={isTripSummary}
              label={flightInfoCartItemLabel}
              headingLink={getStep('flights')}
              headingLinkLabel={cartItemEditLinkLabel}
              headingLinkTrackingLabel="Edit Flights"
              automationHook={withValue(CART_ITEM.hook, 'flight-info')}
            >
              <FlightInfo
                flights={flightOrderItems}
                isTripSummary={isTripSummary}
              />
            </CartItem>
          </ConditionalWrap>
        </ConditionalWrap>

        {isStadium && (
          <CartItem
            isTripSummary={isTripSummary}
            label={stadiumTicketsLabel}
            automationHook={withValue(CART_ITEM.hook, 'Stadium Tickets')}
          >
            {showItem.length && (
              <ShowInfo
                showItem={showItem}
                automationHook={automationHook}
                isTripSummary={isTripSummary}
              />
            )}
          </CartItem>
        )}

        {bundleOrderItems && (
          <CartItem
            showStepLink={!isStadium ? showStepLink('flights') : false}
            isTripSummary={isTripSummary}
            label={bundleCartItemLabel}
            amount={bundleOrderItems ? bundleOrderItems.reduce(
              (sum, seg) => sum + (seg.price?.amount || 0), 0
            ) : undefined}
            headingLink={
              !getStep('flights').isActive ? getStep('flights') : undefined
            }
            headingLinkLabel={
              bundleOrderItems.length > 0 ? cartItemEditLinkLabel : cartItemAddLinkLabel
            }
            headingLinkTrackingLabel={
              bundleOrderItems.length > 0 ? 'Edit Bundles' : 'Add Bundles'
            }
            automationHook={withValue(CART_ITEM.hook, 'bundles')}
          >
            {bundleOrderItems.length > 0 && bundleOrderItems.map((bundleItem, idx) => (
              <>
                <BundleItemBox>
                  <Box mb={2} d="flex" alignItems="center" justifyContent="space-between">
                    <Box d="flex" alignItems="center" flex="1">
                      <Text size={isTripSummary ? (mq.lte('sm') ? 14 : mq.lte('md') ? 13 : 14) : 14} weight={(mq.lte('sm') ? 600 : 500)}>
                        {(bundleItem.flightId === departingFlight?.flight?.id) ? departingLegLabel : returningLegLabel}
                      </Text>
                    {bundleItem.bundle?.banner && !isTripSummary && isMobile && (
                      <BundleRibbon
                        tier={bundleItem.bundle.tier}
                        isMobile={isMobile}
                        isTripSummary={isTripSummary}
                      >
                        <BundleLogo imgSrc={bundleItem.bundle.banner} />
                      </BundleRibbon>
                    )}
                  </Box>
                  {!isTripSummary && isMobile && (
                    <Box mr={3}>
                    <Text size={18} weight={400}>
                      {getPrice(bundleItem.price?.amount).price}
                    </Text>
                    </Box>
                  )}
                  </Box>
                  {bundleItem.bundle && (
                    <BundleInfo
                      bundle={bundleItem.bundle}
                      isTripSummary={isTripSummary}
                      automationHook={withValue(
                        CART_ITEM.hook,
                        (bundleItem.flightId === departingFlight?.flight?.id) ? 'bundles-departing' : 'bundles-returning'
                      )}
                      bundlePrice={bundleItem.price?.amount}
                      isStadium={isStadium}
                    />
                  )}
                </BundleItemBox>
                {bundleOrderItems.length === 2 && idx === 0 && <BundleDivider />}
              </>
            ))}
          </CartItem>
        )}

        <CartItem
          showStepLink={showStepLink('travelers')}
          isTripSummary={isTripSummary}
          label={
            travelers.length === 1
              ? travelerCartItemLabel
              : interpolate(travelersCartItemLabel, {
                number: travelers.length,
              })
          }
          amount={
            hasValidTravelers
              ? ancillaryOrderItemsPriceTotal + ssrsPriceTotal
              : undefined
          }
          headingLink={
            hasValidTravelers && !getStep('travelers').isActive
              ? getStep('travelers')
              : undefined
          }
          withChildrenPadding={false}
          headingLinkLabel={cartItemEditLinkLabel}
          headingLinkTrackingLabel={'Edit Travelers'}
          automationHook={withValue(CART_ITEM.hook, 'travelers')}
        >
          {hasValidTravelers && (
            <TravelersInfo
              flights={flightOrderItems}
              travelers={travelers}
              seats={seatOrderItems}
              ancillaries={ancillaryOrderItems}
              showSeatsLink={showStepLink('seats')}
              showBagsLink={showStepLink('bags')}
              hasBundledBagsAndExtras={bundledBagsAndExtras}
              hasBundledSeats={bundledSeats}
              isTripSummary={isTripSummary}
              seatSelection={
                bundleOrderItems?.length > 0
                  ? bundleOrderItems.map(bundleItem =>
                    bundleItem.bundle?.ancillaries &&
                    isSeatSelectionInBundle(bundleItem.bundle.ancillaries)
                  )
                  : false
              }
            />
          )}
        </CartItem>

        {isHotelAvailable && (
          <CartItem
            showStepLink={showStepLink('hotels')}
            isTripSummary={isTripSummary}
            label={hotelCartItemLabel}
            headingLink={
              !getStep('hotels')?.isActive ? getStep('hotels') : undefined
            }
            headingLinkLabel={
              hotelItem ? cartItemEditLinkLabel : cartItemAddLinkLabel
            }
            headingLinkTrackingLabel={hotelItem ? 'Edit Hotel' : 'Add Hotel'}
            automationHook={withValue(CART_ITEM.hook, 'hotel')}
          >
            {hotelItem && (
              <HotelInfo
                automationHook={automationHook}
                hotelItem={hotelItem}
                isTripSummary={isTripSummary}
              />
            )}
          </CartItem>
        )}

        {isVehicleAvailable && (
          <CartItem
            showStepLink={showStepLink('cars')}
            isTripSummary={isTripSummary}
            label={vehicleCartItemLabel}
            headingLink={
              vehicleItem && !getStep('cars').isActive
                ? getStep('cars')
                : undefined
            }
            headingLinkLabel={cartItemEditLinkLabel}
            headingLinkTrackingLabel="Edit Car"
            automationHook={withValue(CART_ITEM.hook, 'vehicle')}
          >
            {vehicleItem && (
              <CarInfo
                vehicleItem={vehicleItem}
                isTripSummary={isTripSummary}
                vehicleDaysCount={vehicleDaysCount}
                vehicleDaysCountLabel={vehicleDaysCountLabel}
              />
            )}
          </CartItem>
        )}

        {isTripSummary ? null : promos.length ? (
          <Promos promos={promos} isTripSummary={isTripSummary} />
        ) : null}

        {isStadium && !isTripSummary && isMinimumElementsForStadiumPackage && (
          <StadiumBanner
            image={allegiantStadiumLogo}
            label={cartBannerLabel}
            showStadiumBanner={allegiantStadiumLogo || cartBannerLabel}
            automationHook={withValue(automationHook, 'raiders-banner')}
          />
        )}

        {!isTripSummary && (
          <CartFooter
            total={price.total}
            totalDueToday={price.totalDueToday}
            totalDueAtHotelCheckout={price.totalDueAtHotelCheckout}
            hidePrice={isStadium && isMinimumElementsForStadiumPackage}
            isUpliftInitialised={isUpliftInitialised}
            setIsUpliftInitialised={setIsUpliftInitialised}
            paymentDueAt={hotelItem?.paymentDueAt}
            showUplift={shouldUpliftBannerDisplay(upliftSettings, 'cart')}
            isPaymentStep={isPaymentStep}
          />
        )}

        {isTripSummary && isMobile && (
          <ExpandoLabel
            isOpen={isTripSummaryOpen}
            onClick={toggleIsExpanded}
            isFooter={true}
          >
            <TripSummaryButton>
              <Text size={mq.gt('sm') ? 24 : 17} uppercase={true}>
                {translations.paymentPage.tripSummaryTitle}
              </Text>
              <Text
                size={mq.gt('sm') ? 24 : 17}
                uppercase={true}
                automationHook={withValue(
                  automationHook,
                  'trip_summary_amount'
                )}
              >
                ${price.balanceDue.toFixed(2)}
              </Text>
            </TripSummaryButton>
            <ArrowIconContainer isOpen={isTripSummaryOpen}>
              <ArrowIcon />
            </ArrowIconContainer>
          </ExpandoLabel>
        )}
      </CartContent>
    </CartContainer>
  );
};

Cart.propTypes = propTypes;

export default React.memo(Cart);
